import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Col,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
  Upload,
} from "antd";
import { UploadOutlined, CloseOutlined } from "@ant-design/icons";
import { postReq } from "../api.js";
// import { url } from "config/config";
import moment from "moment";
import { message } from "antd";
import * as yup from "yup";

const url = "";
const { TextArea } = Input;
const { Text } = Typography;

const leaveSchema = yup.object().shape({
  leaveType: yup.string().required("Leave type is required"),
  fromDate: yup
    .date()
    .required("From date is required")
    .typeError("Invalid date"),
  toDate: yup
    .date()
    .required("To date is required")
    .min(yup.ref("fromDate"), "To date cannot be before From date")
    .typeError("Invalid date"),
  quantity: yup
    .number()
    .required("Quantity is required")
    .min(1, "Minimum 1 day required"),
  reason: yup.string().required("Reason is required"),
  prescription: yup.mixed().when(["leaveType", "quantity"], {
    is: (leaveType, quantity) => leaveType === "SL" && quantity > 2,
    then: (schema) =>
      schema.required(
        "Prescription is required for Sick Leave more than 2 days",
      ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const leaveTypes = ["PL", "SL", "HL"];

const ApplyLeaveModal = ({ open, edit = null, handleClose }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isUpdating = useRef(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(leaveSchema),
    defaultValues: {
      leaveType: "",
      fromDate: "",
      toDate: "",
      quantity: "",
      reason: "",
      prescription: null,
    },
  });

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");
  const leaveType = watch("leaveType");

  // Auto-calculate quantity from dates
  useEffect(() => {
    if (!fromDate || !toDate) return;
    if (isUpdating.current) return;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (end >= start) {
      isUpdating.current = true;
      const diff = (end - start) / (1000 * 60 * 60 * 24) + 1;
      setValue("quantity", diff, { shouldDirty: true, shouldValidate: true });
      isUpdating.current = false;
    }
  }, [fromDate, toDate]);

  // Half-day logic
  useEffect(() => {
    if (leaveType !== "HL") return;
    if (!fromDate) return;
    if (isUpdating.current) return;

    isUpdating.current = true;
    const formatted = moment(fromDate).format("YYYY-MM-DD");
    setValue("toDate", formatted);
    setValue("quantity", 0.5, { shouldDirty: true });
    isUpdating.current = false;
  }, [leaveType, fromDate]);

  // Populate form on edit
  useEffect(() => {
    if (edit) {
      reset({
        leaveType: edit.leaveType || "",
        fromDate: moment(edit.fromDate).format("YYYY-MM-DD"),
        toDate: moment(edit.toDate).format("YYYY-MM-DD"),
        quantity: edit.quantity,
        reason: edit.reason || "",
        prescription: null,
      });
      setPreviewImage(edit.prescription ? `${url}${edit.prescription}` : null);
    } else {
      reset({
        leaveType: "",
        fromDate: "",
        toDate: "",
        quantity: "",
        reason: "",
        prescription: null,
      });
      setPreviewImage(null);
    }
  }, [edit, reset]);

  const submitHandler = async (data) => {
    // const formData = new FormData();
    // Object.entries(data).forEach(([key, value]) => {
    //   if (value instanceof FileList) {
    //     if (value.length > 0) formData.append(key, value[0]);
    //   } else if (value instanceof Date) {
    //     formData.append(key, moment(value).format("YYYY-MM-DD"));
    //   } else if (value !== null && value !== undefined) {
    //     formData.append(key, value);
    //   }
    // });

    const res = edit
      ? await postReq(`/user/leave/${edit._id}`, { ...data })
      : await postReq("/user/leave", { ...data });

    if (res.status === 200) {
      message.success(
        edit
          ? "Leave updated successfully"
          : "Leave request submitted successfully",
      );
      reset();
      handleClose();
    } else {
      message.error(res.message || "Something went wrong");
    }
  };

  const handleCancel = () => {
    reset();
    handleClose();
  };

  // Helper: wrap field error message
  const errMsg = (field) =>
    errors[field] ? (
      <Text type="danger" style={{ fontSize: 12 }}>
        {errors[field].message}
      </Text>
    ) : null;

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCancel}
        title={edit ? "Edit Leave" : "Apply Leave"}
        closeIcon={<CloseOutlined />}
        width={600}
        styles={{ body: { padding: "16px 24px" } }}
        footer={null}
        destroyOnClose
      >
        <form onSubmit={handleSubmit(submitHandler)}>
          <Row gutter={[0, 16]}>
            {/* Leave Type */}
            <Col span={24}>
              <Controller
                name="leaveType"
                control={control}
                render={({ field }) => (
                  <Form.Item
                    label="Leave Type"
                    validateStatus={errors.leaveType ? "error" : ""}
                    help={errors.leaveType?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      {...field}
                      placeholder="Select leave type"
                      options={leaveTypes.map((t) => ({ label: t, value: t }))}
                    />
                  </Form.Item>
                )}
              />
            </Col>

            {/* From / To Date */}
            <Col span={24}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="From Date"
                    validateStatus={errors.fromDate ? "error" : ""}
                    help={errors.fromDate?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <input
                      type="date"
                      {...register("fromDate")}
                      style={{
                        width: "100%",
                        padding: "7px 11px",
                        borderRadius: 6,
                        border: `1px solid ${errors.fromDate ? "#ff4d4f" : "#d9d9d9"}`,
                        fontSize: 14,
                        outline: "none",
                        color: "inherit",
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="To Date"
                    validateStatus={errors.toDate ? "error" : ""}
                    help={errors.toDate?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <input
                      type="date"
                      {...register("toDate")}
                      disabled={leaveType === "HL"}
                      style={{
                        width: "100%",
                        padding: "7px 11px",
                        borderRadius: 6,
                        border: `1px solid ${errors.toDate ? "#ff4d4f" : "#d9d9d9"}`,
                        fontSize: 14,
                        outline: "none",
                        color: "inherit",
                        backgroundColor:
                          leaveType === "HL" ? "#f5f5f5" : "white",
                        cursor: leaveType === "HL" ? "not-allowed" : "auto",
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            {/* Number of Days */}
            <Col span={24}>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <Form.Item
                    label="Number of Days"
                    validateStatus={errors.quantity ? "error" : ""}
                    help={errors.quantity?.message}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      {...field}
                      disabled
                      style={{ width: "100%" }}
                      placeholder="Auto-calculated"
                    />
                  </Form.Item>
                )}
              />
            </Col>

            {/* Prescription Upload */}
            <Col span={24}>
              <Row gutter={16} align="middle">
                {/* Image Preview */}
                {previewImage && (
                  <Col xs={24} sm={6}>
                    <Image
                      src={
                        previewImage instanceof File
                          ? URL.createObjectURL(previewImage)
                          : previewImage
                      }
                      alt="Prescription"
                      width="100%"
                      style={{ borderRadius: 8, cursor: "pointer" }}
                      preview={{
                        visible: previewOpen,
                        onVisibleChange: setPreviewOpen,
                      }}
                    />
                  </Col>
                )}

                <Col xs={24} sm={previewImage ? 18 : 24}>
                  <Controller
                    name="prescription"
                    control={control}
                    render={({ field }) => (
                      <Form.Item
                        validateStatus={errors.prescription ? "error" : ""}
                        help={errors.prescription?.message}
                        style={{ marginBottom: 0 }}
                      >
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={(file) => {
                            field.onChange(
                              { target: { files: [file] } }?.target?.files ?? [
                                file,
                              ],
                            );
                            setPreviewImage(file);
                            return false; // prevent auto-upload
                          }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: "100%" }}
                          >
                            {previewImage instanceof File
                              ? previewImage.name
                              : "Upload Prescription"}
                          </Button>
                        </Upload>
                      </Form.Item>
                    )}
                  />
                </Col>
              </Row>
            </Col>

            {/* Reason */}
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="Reason"
                  validateStatus={errors.reason ? "error" : ""}
                  help={errors.reason?.message}
                  style={{ marginBottom: 0, width: "100%" }}
                >
                  <TextArea {...field} rows={3} placeholder="Enter reason..." />
                </Form.Item>
              )}
            />
          </Row>

          {/* Footer Actions */}
          <Row justify="end" gutter={8} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={handleCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit">
                {edit ? "Update" : "Submit"}
              </Button>
            </Col>
          </Row>
        </form>
      </Modal>
    </>
  );
};

export default ApplyLeaveModal;
